// Today's three challenges (spec FE-35): on the menu, with the one swap a day, and on the results
// screen, where a round's progress shows once the server has recorded it. Progress is only ever
// what the server says.
import { useCallback, useEffect, useState } from 'react';
import { errorText } from '../App';
import { api, type ChallengeState } from '../lib/api';
import { parkNaira } from '../lib/money';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { Html, W } from './wahala';

/** One array for every render: a fresh [] each time would re-run the effect and refetch forever. */
const NEVER: readonly number[] = [];

export function Challenges({
  swappable = true,
  refreshAfterMs = NEVER,
}: {
  swappable?: boolean;
  refreshAfterMs?: readonly number[];
}) {
  const set = useUi((s) => s.set);
  const [state, setState] = useState<ChallengeState | null>(null);
  const [asking, setAsking] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .challenges()
      .then(setState)
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // after a round, the persist worker records it within a moment: look again then
    const timers = refreshAfterMs.map((ms) => setTimeout(load, ms));
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [load, refreshAfterMs]);

  if (!state) return null;
  return (
    <>
      {asking !== null && (
        <section className="card swap-ask" role="alertdialog" aria-labelledby="swap-q">
          <p id="swap-q">{S.challenges.confirm(parkNaira(state.swapPrice))}</p>
          <div className="row">
            <button
              type="button"
              className="wh-btn wh-btn--carry wh-btn--md"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  setState(await api.swapChallenge(asking));
                  set({ notice: S.challenges.swapped, noticeTone: 'done' });
                } catch (e) {
                  set({ notice: errorText(e) });
                } finally {
                  setAsking(null);
                  setBusy(false);
                }
              }}
            >
              {S.challenges.yes}
            </button>
            <button type="button" className="wh-btn wh-btn--quiet wh-btn--md" onClick={() => setAsking(null)}>
              {S.challenges.no}
            </button>
          </div>
        </section>
      )}
      <Html
        onClick={(e) => {
          const slot = (e.target as HTMLElement).closest('[data-swap]')?.getAttribute('data-swap');
          if (slot && swappable) setAsking(Number(slot));
        }}
        html={W().challenges({
          title: S.challenges.title,
          resets: S.challenges.resets,
          items: state.challenges,
          swap: { price: state.swapPrice, used: state.swapUsed },
          swappable,
          disabled: busy || asking !== null,
        })}
      />
    </>
  );
}
